import type {
    ConditionInput,
    GetDripSequence_campaign_goal,
    GetDripSequence_campaign_startTrigger as StartTrigger,
    GetDripSequence_campaign_startTrigger_conditions as StartTriggerCondition,
    GetDripSequence_campaign_startTrigger_conditions_config_ConditionConfigEntityFilter_filter as EntityFilterConfig,
    EntityFilterFieldConfig_EntityFilterFieldConfigRelationship,
    EntityFilterFieldConfig_EntityFilterFieldConfigEnum,
} from 'nutshell-graphql-types';

// These _values_ are type checked from the server, but its nice to have
// them in constants in case they change, and its slightly safer.

// Start trigger types
const ADD_TAG = 'ADD_TAG';
const ADD_TO_AUDIENCE = 'ADD_TO_AUDIENCE';
const LEAD_STAGE_ENTERED = 'LEAD_STAGE_ENTERED';

// Start trigger / goal types
const LEAD_CREATED = 'LEAD_CREATED';
const LEAD_CLOSED = 'LEAD_CLOSED';

// Goal types
const LEAD_WON = 'LEAD_WON';
const LINK_CLICKED = 'LINK_CLICKED';

// Next step types
const NEXT_STEP_ADD_TAGS = 'ADD_TAGS';
const NEXT_STEP_ADD_AUDIENCES = 'ADD_AUDIENCES';
const NEXT_STEP_REMOVE_AUDIENCES = 'REMOVE_AUDIENCES';

const CONDITION_TYPE_ENTITY_FILTER = 1;
const CONDITION_TYPE_ADD_TO_AUDIENCE = 3;
const CONDITION_TYPE_ADD_TAG = 4;

export const transformTriggerConditionsToState = (startTrigger: StartTrigger) => {
    if (startTrigger.type === ADD_TO_AUDIENCE || startTrigger.type === ADD_TAG) {
        // @ts-expect-error
        const config = startTrigger.conditions[0].config;

        if (
            config.__typename === 'ConditionConfigAudience' ||
            config.__typename === 'ConditionConfigTag'
        ) {
            // @ts-expect-error
            return startTrigger.conditions[0].config;
        }
    }

    // @ts-expect-error
    const entityFilterConfigs: EntityFilterConfig[] = startTrigger.conditions
        .map((condition: StartTriggerCondition) => {
            return condition.config.__typename === 'ConditionConfigEntityFilter'
                ? condition.config.filter
                : null;
        })
        .filter((conditionFilter) => conditionFilter);

    // If no valid filters, return nothing
    if (entityFilterConfigs.length === 0) {
        return null;
    }

    if (startTrigger.type === LEAD_CREATED) {
        const leadCreatedFilter = entityFilterConfigs[0];
        // @ts-expect-error
        if (leadCreatedFilter.__typename === 'EntityFilterFieldConfigRelationship')
            return {
                // @ts-expect-error
                leadCreated: leadCreatedFilter.relationshipData[0],
            };
    }
    // Support legacy backend conditions
    else if (startTrigger.type === ADD_TO_AUDIENCE) {
        const audienceFilter = entityFilterConfigs[0];

        // @ts-expect-error
        if (audienceFilter.__typename === 'EntityFilterFieldConfigRelationship') {
            return {
                // @ts-expect-error
                audiences: audienceFilter.relationshipData,
            };
        }
    }
    // Support legacy backend conditions
    else if (startTrigger.type === ADD_TAG) {
        const tagFilter = entityFilterConfigs[0];

        // @ts-expect-error
        if (tagFilter.__typename === 'EntityFilterFieldConfigRelationship') {
            return {
                // @ts-expect-error
                tags: tagFilter.relationshipData,
            };
        }
    } else if (startTrigger.type === LEAD_STAGE_ENTERED) {
        const stageEnteredFilter = entityFilterConfigs[0];

        // @ts-expect-error
        if (stageEnteredFilter.__typename === 'EntityFilterFieldConfigRelationship') {
            return {
                // @ts-expect-error
                stageEntered: [{stage: stageEnteredFilter.relationshipData[0]}],
            };
        }
    } else if (startTrigger.type === LEAD_CLOSED) {
        // @ts-expect-error
        const pipelineFilter:
            | EntityFilterFieldConfig_EntityFilterFieldConfigRelationship
            | null
            | undefined = entityFilterConfigs.find(
            (f) => f.__typename === 'EntityFilterFieldConfigRelationship'
        );

        // @ts-expect-error
        const statusFilter: EntityFilterFieldConfig_EntityFilterFieldConfigEnum | null | undefined =
            entityFilterConfigs.find((f) => f.__typename === 'EntityFilterFieldConfigEnum');

        return {
            leadClosed: [
                {
                    // @ts-expect-error
                    stageset: pipelineFilter.relationshipData,
                    // @ts-expect-error
                    status: statusFilter.enumValue,
                },
            ],
        };
    }
};

function createConditionFromFilter(filterValue: any): ConditionInput {
    return {
        config: {
            filter: filterValue,
        },
        type: CONDITION_TYPE_ENTITY_FILTER,
    };
}

function createConditionFromAudienceList(audienceList: string[]): ConditionInput {
    return {
        config: {
            audiences: audienceList,
        },
        type: CONDITION_TYPE_ADD_TO_AUDIENCE,
    };
}

function createConditionFromTagList(tagList: string[]): ConditionInput {
    return {
        config: {
            tags: tagList,
        },
        type: CONDITION_TYPE_ADD_TAG,
    };
}

export function formDataToTriggerConditionsArray(formData: any): any {
    const triggerType = formData.triggerType;
    const conditions = [];

    if (triggerType === ADD_TAG) {
        // @ts-expect-error
        const tagList = formData.tags.map((tagId) => {
            return tagId;
        });
        const condition = createConditionFromTagList(tagList);
        conditions.push(condition);
    } else if (triggerType === LEAD_CREATED) {
        const leadCreatedData = formData.leadCreated;
        const relationshipData = [leadCreatedData];
        const condition = createConditionFromFilter({
            fieldName: 'stageset',
            fieldType: 'RELATIONSHIP',
            relationshipConfig: {
                relationshipAnyAll: 'ANY',
                relationshipData: relationshipData,
            },
        });
        conditions.push(condition);
    } else if (triggerType === LEAD_STAGE_ENTERED) {
        const stageEnteredData = formData.stageEntered[0];
        const relationshipData = stageEnteredData.stage;
        const condition = createConditionFromFilter({
            fieldName: 'milestone',
            fieldType: 'RELATIONSHIP',
            relationshipConfig: {
                relationshipAnyAll: 'ANY',
                relationshipData: relationshipData,
            },
        });
        conditions.push(condition);
    } else if (triggerType === LEAD_CLOSED) {
        const leadClosedData = formData.leadClosed[0];
        const relationshipData = leadClosedData.stageset;
        const stagesetCondition = createConditionFromFilter({
            fieldName: 'stageset',
            fieldType: 'RELATIONSHIP',
            relationshipConfig: {
                relationshipAnyAll: 'ANY',
                relationshipData: relationshipData,
            },
        });
        const statusCondition = createConditionFromFilter({
            fieldName: 'status',
            fieldType: 'ENUM',
            enumConfig: {enumValue: leadClosedData.status, enumIsNot: false},
        });
        conditions.push(stagesetCondition, statusCondition);
    } else if (triggerType === ADD_TO_AUDIENCE) {
        // @ts-expect-error
        const audienceList = formData.audiences.map((audienceId) => {
            return audienceId;
        });
        const condition = createConditionFromAudienceList(audienceList);
        conditions.push(condition);
    }

    return conditions;
}

export const transformGoalConditionsToState = (goal: GetDripSequence_campaign_goal) => {
    const conditionConfigs = goal.conditions.map((condition) => {
        return condition.config;
    });

    // No goals use multiple conditions
    const firstConditionConfig = conditionConfigs[0];

    // @ts-expect-error
    if (firstConditionConfig.__typename === 'ConditionConfigLink') {
        if (goal.type === LINK_CLICKED) {
            return {
                // @ts-expect-error
                linkClicked: firstConditionConfig.links,
            };
        }
        // @ts-expect-error
    } else if (firstConditionConfig.__typename === 'ConditionConfigTag') {
        return {
            // @ts-expect-error
            tags: firstConditionConfig.tags,
        };
        // @ts-expect-error
    } else if (firstConditionConfig.__typename === 'ConditionConfigAudience') {
        return {
            // @ts-expect-error
            audiences: firstConditionConfig.audiences,
        };
        // @ts-expect-error
    } else if (firstConditionConfig.__typename === 'ConditionConfigEntityFilter') {
        // @ts-expect-error
        const filter = firstConditionConfig.filter;
        if (!filter) {
            return null;
        }

        if (goal.type === LEAD_WON && filter.__typename === 'EntityFilterFieldConfigRelationship') {
            return {
                leadWon: filter.relationshipData[0],
            };
        } else if (
            // Support legacy conditions
            goal.type === ADD_TAG &&
            filter.__typename === 'EntityFilterFieldConfigRelationship'
        ) {
            return {
                tags: filter.relationshipData,
            };
        } else if (
            goal.type === LEAD_CREATED &&
            filter.__typename === 'EntityFilterFieldConfigRelationship'
        ) {
            return {
                leadCreated: filter.relationshipData[0],
            };
        }
    }
};

export const formDataToGoalConditionsArray = (formData: any): any => {
    const goalType = formData.goalType;
    const conditions = [];

    if (goalType === LEAD_WON) {
        const leadWonData = formData.leadWon;
        const relationshipData = [leadWonData];
        const condition = createConditionFromFilter({
            fieldName: 'stageset',
            fieldType: 'RELATIONSHIP',
            relationshipConfig: {
                relationshipAnyAll: 'ANY',
                relationshipData: relationshipData,
            },
        });
        conditions.push(condition);
    } else if (goalType === ADD_TAG) {
        // @ts-expect-error
        const tagList = formData.tags.map((tagId) => {
            return tagId;
        });
        const condition = createConditionFromTagList(tagList);
        conditions.push(condition);
    } else if (goalType === ADD_TO_AUDIENCE) {
        // @ts-expect-error
        const audiencesList = formData.audiences.map((audienceId) => {
            return audienceId;
        });
        const condition = createConditionFromAudienceList(audiencesList);
        conditions.push(condition);
    } else if (goalType === LEAD_CREATED) {
        const leadCreatedData = formData.leadCreated;
        const relationshipData = [leadCreatedData];
        const condition = createConditionFromFilter({
            fieldName: 'stageset',
            fieldType: 'RELATIONSHIP',
            relationshipConfig: {
                relationshipAnyAll: 'ANY',
                relationshipData: relationshipData,
            },
        });
        conditions.push(condition);
    } else if (goalType === LINK_CLICKED) {
        const condition = {
            config: {
                // This is an array of actual links, not ids
                links: formData.linkClicked,
            },
        } as const;

        conditions.push(condition);
    }

    return conditions;
};

export const transformNextStepConfigToState = (nextStep: any) => {
    const config = nextStep.config;

    if (nextStep.type === NEXT_STEP_ADD_TAGS) {
        // @ts-expect-error
        const tags = config.tags.map((tag) => tag.id);

        return {
            tags: tags,
        };
    } else if (nextStep.type === NEXT_STEP_ADD_AUDIENCES) {
        // @ts-expect-error
        const audiences = config.audiences.map((audience) => audience.id);

        return {
            audiences: audiences,
        };
    } else if (nextStep.type === NEXT_STEP_REMOVE_AUDIENCES) {
        // @ts-expect-error
        const audiences = config.audiences.map((audience) => audience.id);

        return {
            audiences: audiences,
        };
    }
};

export const existingGoalToConditionsArray = (goal: GetDripSequence_campaign_goal) => {
    const goalType = goal.type;
    // @ts-expect-error
    const conditionConfig = goal.conditions[0].config;
    const conditions = [];

    if (conditionConfig.__typename === 'ConditionConfigLink') {
        const condition = {
            config: {
                // This is an array of actual links, not ids
                links: conditionConfig.links,
            },
        } as const;

        conditions.push(condition);
    } else if (
        conditionConfig.__typename === 'ConditionConfigEntityFilter' &&
        conditionConfig.filter.__typename === 'EntityFilterFieldConfigRelationship'
    ) {
        const relationshipData = conditionConfig.filter.relationshipData;

        if (goalType === LEAD_WON) {
            const condition = createConditionFromFilter({
                fieldName: 'stageset',
                fieldType: 'RELATIONSHIP',
                relationshipConfig: {
                    relationshipAnyAll: 'ANY',
                    relationshipData: relationshipData,
                },
            });
            conditions.push(condition);
        } else if (goalType === LEAD_CREATED) {
            const condition = createConditionFromFilter({
                fieldName: 'stageset',
                fieldType: 'RELATIONSHIP',
                relationshipConfig: {
                    relationshipAnyAll: 'ANY',
                    relationshipData: relationshipData,
                },
            });
            conditions.push(condition);
        }
    } else if (conditionConfig.__typename === 'ConditionConfigAudience') {
        const audiencesList = conditionConfig.audiences.map((audienceId) => {
            return audienceId;
        });
        const condition = createConditionFromAudienceList(audiencesList);
        conditions.push(condition);
    } else if (conditionConfig.__typename === 'ConditionConfigTag') {
        const tagIds = conditionConfig.tags.map((tagId) => {
            return tagId;
        });

        const condition = createConditionFromTagList(tagIds);
        conditions.push(condition);
    }

    return conditions;
};

export const formDataToNextStepAction = (formData: any): any => {
    const nextStepType = formData.nextStepType;
    let action;

    if (nextStepType === NEXT_STEP_ADD_TAGS) {
        // @ts-expect-error
        const tagData = formData.tags.map((tagId) => {
            return tagId;
        });
        action = {
            addTagIds: tagData,
        };
    } else if (nextStepType === NEXT_STEP_ADD_AUDIENCES) {
        // @ts-expect-error
        const audienceData = formData.audiences.map((audienceId) => {
            return audienceId;
        });
        action = {
            addAudienceIds: audienceData,
        };
    } else if (nextStepType === NEXT_STEP_REMOVE_AUDIENCES) {
        // @ts-expect-error
        const audienceData = formData.audiences.map((audienceId) => {
            return audienceId;
        });
        action = {
            removeAudienceIds: audienceData,
        };
    }

    return {config: action};
};
